import React, { useEffect, useState, useMemo } from 'react';
import authAxios from '../utils/authAxios';
import StandardTableLayout from '../styles/StandardTableLayout';
import {
  Input,
  Select,
  Button,
  ConfirmDelete,
  SecondaryButton,
} from '../styles/TableStyles';
import { mapColumnLabels } from '../styles/ColumnUtils';
import { savedToast, errorToast, deletedToast } from '../utils/toastUtils';

const READONLY_STYLE = { backgroundColor: 'var(--grey-100)', cursor: 'not-allowed' };

const initialFormState = {
  BagName: '',
  BagType: '',
  BagWeight: '',
  Count: '',
  CostPerBag: '',
  BagLength: '',
  WastePercentage: '',
  LaborCost: '', // derived server-side (read-only)
  TotalCost: '', // computed column (read-only)
};

const visibleColumns = mapColumnLabels([
  'BagName',
  'BagType',
  'BagWeight',
  'Count',
  'CostPerBag',
  'BagLength',
  'WastePercentage',
  'LaborCost',
  'TotalCost',
]);

const fmt = (v, dp) =>
  (v !== null && v !== '' && !isNaN(parseFloat(v)) ? `$${parseFloat(v).toFixed(dp)}` : '');

const BagCostsPage = () => {
  const [bagCosts, setBagCosts] = useState([]);
  const [packTypes, setPackTypes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    fetchBagCosts();
    fetchPackTypes();
  }, []);

  const fetchBagCosts = () => {
    authAxios.get('/api/bagcosts')
      .then(res => setBagCosts(res.data))
      .catch(err => console.error('Error fetching BagCosts:', err));
  };

  const fetchPackTypes = () => {
    authAxios.get('/api/packtypes')
      .then(res => setPackTypes(res.data))
      .catch(err => console.error('Error fetching PackTypes:', err));
  };

  // Only Bag product-type styles can carry a bag cost.
  const bagStyles = useMemo(
    () => packTypes.filter(pt => pt.ProductType === 'Bag'),
    [packTypes]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Selecting a Style auto-fills its group (Type) into BagType.
  const handleBagNameChange = (e) => {
    const style = e.target.value;
    const pt = packTypes.find(p => p.Style === style);
    setFormData(prev => ({ ...prev, BagName: style, BagType: pt?.Type ?? '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      // LaborCost + TotalCost are server-owned; never sent.
      const editable = ['BagName', 'BagType', 'BagWeight', 'Count', 'CostPerBag', 'BagLength', 'WastePercentage'];
      const payload = {};
      editable.forEach(k => { payload[k] = formData[k] === '' ? null : formData[k]; });

      if (isEditing) {
        await authAxios.put(`/api/bagcosts/${editingId}`, payload);
      } else {
        await authAxios.post('/api/bagcosts', payload);
      }

      setFormData(initialFormState);
      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      fetchBagCosts();
      savedToast('Bag Cost');
    } catch (err) {
      const msg = err.response?.data?.error;
      console.error('Error submitting BagCost:', msg || err);
      errorToast(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (row) => {
    setFormData({
      BagName: row.BagName ?? '',
      BagType: row.BagType ?? '',
      BagWeight: row.BagWeight ?? '',
      Count: row.Count ?? '',
      CostPerBag: row.CostPerBag ?? '',
      BagLength: row.BagLength ?? '',
      WastePercentage: row.WastePercentage ?? '',
      LaborCost: row.LaborCost ?? '',
      TotalCost: row.TotalCost ?? '',
    });
    setShowForm(false);
    setIsEditing(true);
    setEditingId(row.UniqueID);
  };

  const handleDelete = async (id) => {
    try {
      await authAxios.delete(`/api/bagcosts/${id}`);
      fetchBagCosts();
      deletedToast('Bag Cost');
    } catch (err) {
      console.error('Error deleting BagCost:', err);
    }
  };

  const sortedData = useMemo(() => {
    const sortable = [...bagCosts];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const valA = a[sortConfig.key];
        const valB = b[sortConfig.key];
        if (typeof valA === 'number' && typeof valB === 'number') {
          return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
        }
        return sortConfig.direction === 'asc'
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      });
    }
    return sortable;
  }, [bagCosts, sortConfig]);

  const filteredData = useMemo(() => {
    const lower = searchQuery.toLowerCase();
    return sortedData.filter(row =>
      Object.values(row).some(val =>
        String(val).toLowerCase().includes(lower)
      )
    );
  }, [searchQuery, sortedData]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  return (
    <StandardTableLayout
      title="Bag Costs"
      showForm={showForm}
      editingId={editingId}
      submitting={submitting}
      onToggleForm={() => {
        setShowForm(!showForm);
        setIsEditing(false);
        setFormData(initialFormState);
      }}
      onSubmit={handleSubmit}
      columns={visibleColumns}
      formData={formData}
      handleChange={handleChange}
      rows={filteredData}
      searchValue={searchQuery}
      onSearchChange={(e) => setSearchQuery(e.target.value)}
      sortConfig={sortConfig}
      onSort={handleSort}
      renderCell={(col, data, handleChange) => {
        if (!handleChange) {
          let text;
          if (col === 'CostPerBag' || col === 'TotalCost') text = fmt(data?.[col], 2);
          else if (col === 'LaborCost') text = fmt(data?.[col], 4);
          else text = String(data?.[col] ?? '');
          return (
            <span style={{ background: 'transparent', display: 'inline-block', padding: '4px 0' }}>
              {text}
            </span>
          );
        }
        if (col === 'BagName') {
          return (
            <Select name="BagName" aria-label="Style" value={data.BagName || ''} onChange={handleBagNameChange}>
              <option value="">Select Style</option>
              {bagStyles.map(pt => (
                <option key={pt.UniqueID} value={pt.Style}>{pt.Style}</option>
              ))}
            </Select>
          );
        }
        if (col === 'BagType') {
          return (
            <Input type="text" name="BagType" value={data.BagType ?? ''} readOnly placeholder="(from style)" style={READONLY_STYLE} />
          );
        }
        if (col === 'LaborCost' || col === 'TotalCost') {
          return (
            <Input type="text" name={col} value={data[col] ?? ''} readOnly placeholder="(auto)" style={READONLY_STYLE} />
          );
        }
        const placeholders = { BagWeight: 'lb', Count: 'ct', CostPerBag: '$0.00', BagLength: '0.0', WastePercentage: '0.05 = 5%' };
        const steps = { BagWeight: '0.01', Count: '1', CostPerBag: '0.0001', BagLength: '0.1', WastePercentage: '0.0001' };
        return (
          <Input
            type="number"
            step={steps[col]}
            name={col}
            value={data[col] ?? ''}
            placeholder={placeholders[col]}
            onChange={handleChange}
          />
        );
      }}
      renderActions={(row) => (
        editingId === row.UniqueID ? (
          <>
            <Button type="submit" disabled={submitting}>Save</Button>
            <SecondaryButton
              type="button"
              onClick={() => {
                setIsEditing(false);
                setEditingId(null);
                setFormData(initialFormState);
              }}
            >
              Cancel
            </SecondaryButton>
          </>
        ) : (
          <>
            <SecondaryButton type="button" onClick={() => handleEdit(row)}>Edit</SecondaryButton>
            <ConfirmDelete onConfirm={() => handleDelete(row.UniqueID)} />
          </>
        )
      )}
    />
  );
};

export default BagCostsPage;
